import { useMemo, useState } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2, Plus, RefreshCw, Save, Trash2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  type ColumnDef,
  SortableHeader,
  filterItems,
  renderColumnFilter,
  sortItems,
  usePersistedColumnFilters,
  usePersistedColumnVisibility,
} from './tenderTableUtils';
import { TenderTableToolbar } from './TenderTableToolbar';

export type KeywordCategories = Record<string, string[]>;

interface KeywordRow {
  category: string;
  keyword: string;
}

const FILTER_STORAGE_KEY = 'atek-keywords-column-filters';
const VISIBILITY_STORAGE_KEY = 'atek-keywords-column-visibility';
const PAGE_SIZE = 100;

function flattenCategories(categories: KeywordCategories): KeywordRow[] {
  const rows: KeywordRow[] = [];
  for (const [category, words] of Object.entries(categories)) {
    for (const word of words) rows.push({ category, keyword: word });
  }
  return rows;
}

interface KeywordsTableProps {
  categories: KeywordCategories;
  isLoading: boolean;
  isError: boolean;
  isRefreshing?: boolean;
  hasChanges: boolean;
  isSaving: boolean;
  onRefresh: () => void;
  onChange: (next: KeywordCategories) => void;
  onSave: () => void;
  onDiscard: () => void;
}

export function KeywordsTable({
  categories,
  isLoading,
  isError,
  isRefreshing,
  hasChanges,
  isSaving,
  onRefresh,
  onChange,
  onSave,
  onDiscard,
}: KeywordsTableProps) {
  const [sortKey, setSortKey] = useState<string | null>('category');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const [page, setPage] = useState(0);
  const [newKeywordByCategory, setNewKeywordByCategory] = useState<Record<string, string>>({});
  const [newCategoryName, setNewCategoryName] = useState('');

  const rows = useMemo(() => flattenCategories(categories), [categories]);

  const categoryOptions = useMemo(() => {
    const set = new Set(Object.keys(categories));
    return Array.from(set).sort().map((v) => ({ label: v.replace(/_/g, ' '), value: v }));
  }, [categories]);

  const removeKeyword = (category: string, keyword: string) => {
    const next = { ...categories, [category]: (categories[category] || []).filter((w) => w !== keyword) };
    onChange(next);
  };

  const removeCategory = (category: string) => {
    const next = { ...categories };
    delete next[category];
    onChange(next);
  };

  const addKeyword = (category: string) => {
    const val = (newKeywordByCategory[category] || '').trim();
    if (!val) return;
    const existing = categories[category] || [];
    if (existing.includes(val)) return;
    onChange({ ...categories, [category]: [...existing, val] });
    setNewKeywordByCategory((prev) => ({ ...prev, [category]: '' }));
  };

  const addCategory = () => {
    const key = newCategoryName.trim().toLowerCase().replace(/\s+/g, '_');
    if (!key || categories[key]) return;
    onChange({ ...categories, [key]: [] });
    setNewCategoryName('');
  };

  const columns: ColumnDef<KeywordRow>[] = useMemo(() => ([
    {
      key: 'category',
      label: 'Category',
      sortKey: 'category',
      filterType: 'select',
      filterOptions: categoryOptions,
      getValue: (r) => r.category,
      render: (r) => <span className="text-sm font-medium capitalize">{r.category.replace(/_/g, ' ')}</span>,
      headerClassName: 'w-[220px]',
    },
    {
      key: 'keyword',
      label: 'Keyword',
      sortKey: 'keyword',
      filterType: 'text',
      getValue: (r) => r.keyword,
      render: (r) => <span className="text-sm">{r.keyword}</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      noFilter: true,
      align: 'right',
      getValue: () => '',
      headerClassName: 'w-[80px] text-right',
      render: (r) => (
        <button
          className="p-1 rounded text-muted-foreground hover:text-red-600 transition-colors"
          title="Remove keyword"
          onClick={() => removeKeyword(r.category, r.keyword)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [categoryOptions, categories]);

  const {
    columnFilters, setColumnFilter, clearColumnFilters, hasActiveColumnFilters,
  } = usePersistedColumnFilters(FILTER_STORAGE_KEY);

  const { toggleColumn, isVisible } = usePersistedColumnVisibility(VISIBILITY_STORAGE_KEY, columns);

  const visibleColumns = useMemo(() => columns.filter((c) => c.noFilter || isVisible(c.key)), [columns, isVisible]);

  const filteredItems = useMemo(() => filterItems(rows, columns, columnFilters), [rows, columns, columnFilters]);
  const sortedItems = useMemo(() => sortItems(filteredItems, columns, sortKey, sortDirection), [filteredItems, columns, sortKey, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedItems = useMemo(
    () => sortedItems.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [sortedItems, currentPage],
  );

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDirection((d) => (d === 1 ? -1 : 1));
    else { setSortKey(key); setSortDirection(1); }
    setPage(0);
  };

  const categoriesWithEmptyAppended = useMemo(() => {
    const list = Object.keys(categories).sort();
    return list;
  }, [categories]);

  const extraToolbar = (
    <>
      <form
        className="flex items-center gap-1"
        onSubmit={(e) => { e.preventDefault(); addCategory(); }}
      >
        <Input
          className="h-8 text-xs w-40"
          placeholder="New category..."
          value={newCategoryName}
          onChange={(e) => setNewCategoryName(e.target.value)}
        />
        <Button type="submit" size="sm" variant="outline" disabled={!newCategoryName.trim()}>
          <Plus className="h-3 w-3 mr-1" /> Category
        </Button>
      </form>
      {hasChanges && (
        <>
          <Button size="sm" variant="ghost" onClick={onDiscard}>Discard</Button>
          <Button size="sm" variant="default" onClick={onSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
            Save
          </Button>
        </>
      )}
    </>
  );

  return (
    <div className="border rounded-lg overflow-hidden">
      <TenderTableToolbar
        items={sortedItems}
        columns={columns}
        csvFilename="keywords"
        hasActiveColumnFilters={hasActiveColumnFilters}
        onClearColumnFilters={clearColumnFilters}
        isVisible={isVisible}
        toggleColumn={toggleColumn}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        countLabel={
          `${Object.keys(categories).length} categories · ${rows.length} keywords`
          + (sortedItems.length !== rows.length ? ` · showing ${sortedItems.length}` : '')
        }
        extra={extraToolbar}
      />
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              {visibleColumns.map((col) => renderColumnFilter(col, columnFilters, setColumnFilter))}
            </TableRow>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(col.align === 'right' && 'text-right', col.headerClassName)}
                >
                  {col.sortKey ? (
                    <SortableHeader
                      label={col.label}
                      sortKey={col.sortKey}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      align={col.align}
                    />
                  ) : (
                    col.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={`skeleton-${i}`}>
                {visibleColumns.map((col) => (
                  <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                ))}
              </TableRow>
            ))}
            {!isLoading && isError && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="py-8 text-center">
                  <p className="text-red-600">Failed to load keywords.</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={onRefresh}>
                    <RefreshCw className="h-3 w-3 mr-1" /> Retry
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="py-8 text-center text-muted-foreground">
                  No keywords yet. Add a category and some keywords to get started.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && pagedItems.length === 0 && rows.length > 0 && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="py-8 text-center text-muted-foreground">
                  No keywords match the filters
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && pagedItems.map((r) => (
              <TableRow key={`${r.category}-${r.keyword}`} className="hover:bg-muted/50">
                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={cn(
                      'text-sm align-middle',
                      col.align === 'right' && 'text-right',
                      col.className,
                    )}
                  >
                    {col.render ? col.render(r) : (String(col.getValue(r) ?? '') || '—')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Inline add-keyword-per-category rows */}
      {!isLoading && !isError && categoriesWithEmptyAppended.length > 0 && (
        <div className="border-t bg-muted/10 px-3 py-2">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1.5">Add keyword</p>
          <div className="flex flex-wrap gap-2">
            {categoriesWithEmptyAppended.map((category) => (
              <form
                key={category}
                className="flex items-center gap-1"
                onSubmit={(e) => { e.preventDefault(); addKeyword(category); }}
              >
                <span className="text-xs text-muted-foreground capitalize min-w-[80px]">{category.replace(/_/g, ' ')}</span>
                <Input
                  className="h-7 w-32 text-xs"
                  placeholder="keyword..."
                  value={newKeywordByCategory[category] || ''}
                  onChange={(e) => setNewKeywordByCategory((prev) => ({ ...prev, [category]: e.target.value }))}
                />
                <Button type="submit" size="sm" variant="ghost" className="h-7 px-2" disabled={!(newKeywordByCategory[category] || '').trim()}>
                  <Plus className="h-3 w-3" />
                </Button>
                {(categories[category] || []).length === 0 && (
                  <button
                    type="button"
                    className="p-1 rounded text-muted-foreground hover:text-red-600 transition-colors"
                    title="Remove empty category"
                    onClick={() => removeCategory(category)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </form>
            ))}
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="border-t px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {currentPage + 1} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 0}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
